<template>
    <div class="ac-page">
        <!-- Header -->
        <div class="ac-header">
            <div class="ac-header-left">
                <div class="ac-icon-wrap"><i class="fas fa-layer-group"></i></div>
                <h2 class="ac-title">Asset Categories</h2>
                <span class="ac-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <div class="ac-header-right">
                <AllowPermission :permission="PERMISSION_CODES.EDIT_ASSET_CATEGORIES">
                    <button class="ac-add-btn" @click="showForm = true">
                        <i class="fas fa-plus"></i>
                        Add Category
                    </button>
                </AllowPermission>
            </div>
        </div>

        <!-- Form Modal -->
        <RightSideModal :show="showForm" :title="editMode ? 'Edit Category' : 'New Category'" @close="closeForm">
            <Form :fields="createFields" title="Asset Category" submitUrl="/asset-categories"
                updateRootUrl="/asset-categories" @success="handleFormSuccess" @cancel="closeForm"
                :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <!-- Loading -->
        <div v-if="loading" class="ac-loading"><div class="ac-spinner"></div><span>Loading...</span></div>

        <!-- Empty -->
        <div v-else-if="!items.length" class="ac-empty">
            <div class="ac-empty-icon"><i class="fas fa-layer-group"></i></div>
            <h3>No asset categories found</h3>
        </div>

        <!-- Cards Grid -->
        <div v-else class="ac-grid">
            <div class="ac-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="ac-card-top">
                    <div class="ac-card-icon" :class="acColor(idx)"><i class="fas fa-tag"></i></div>
                    <div class="ac-card-info">
                        <h4 class="ac-card-name">{{ item.name }}</h4>
                    </div>
                </div>
                <div class="ac-card-details">
                    <div class="ac-detail">
                        <span class="ac-detail-label">Depreciation / Year</span>
                        <span class="ac-detail-val">{{ item.depreciationPercentPerYear }}%</span>
                    </div>
                </div>
                <div class="ac-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_ASSET_CATEGORIES">
                        <button class="ac-action-btn ac-btn-edit" @click="enableEditing(item)"><i class="fas fa-pen"></i> Edit</button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_ASSET_CATEGORIES">
                        <button class="ac-action-btn ac-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { createField } from "../../utils/common"
import { PERMISSION_CODES } from "../../store/permission"
import { http } from "../../utils/http"

export default {
    data() {
        return {
            PERMISSION_CODES,
            createFields: [
                createField("Name*", "name", "text", true),
                createField("Depreciation % Per Year*", "depreciationPercentPerYear", "number", true),
            ],
            items: [],
            loading: true,
            showForm: false,
            editMode: false,
            editData: null,
        }
    },
    components: { Form, RightSideModal, AllowPermission },
    mounted() { this.fetchData() },
    methods: {
        acColor(i) { return ["ac-clr-blue","ac-clr-purple","ac-clr-teal","ac-clr-amber","ac-clr-rose"][i % 5] },
        closeForm() { this.showForm = false; this.editMode = false; this.editData = null },
        handleFormSuccess() { this.closeForm(); this.fetchData() },
        enableEditing(item) { this.editMode = true; this.editData = { ...item }; this.showForm = true; window.scrollTo({ top: 0, behavior: "smooth" }) },
        async fetchData() {
            this.loading = true
            try {
                const res = await http.get("/asset-categories")
                this.items = res.data.result || []
            } catch (err) { console.log("error fetching asset categories", err) }
            this.loading = false
        },
        async deleteItem(id) {
            if (!confirm("Delete this category?")) return
            try {
                await http.delete(`/asset-categories/${id}`)
                this.$toast.success("Deleted")
                this.items = this.items.filter(c => c.id !== id)
            } catch (e) { this.$toast.error("Error deleting category") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.ac-page { animation: acFade .3s ease; }
@keyframes acFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.ac-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.ac-header-left { display:flex; align-items:center; gap:10px; }
.ac-header-right { display:flex; align-items:center; gap:10px; }
.ac-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.ac-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.ac-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.ac-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }

/* Loading / Empty */
.ac-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.ac-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:acSpin .7s linear infinite; }
@keyframes acSpin { to { transform:rotate(360deg); } }
.ac-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.ac-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }

/* Grid */
.ac-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.ac-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:acSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes acSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.ac-card-top { display:flex; align-items:center; gap:10px; }
.ac-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.ac-clr-blue { background:#eff6ff; color:#2563eb; }
.ac-clr-purple { background:#f5f3ff; color:#7c3aed; }
.ac-clr-teal { background:#f0fdfa; color:#0d9488; }
.ac-clr-amber { background:#fffbeb; color:#d97706; }
.ac-clr-rose { background:#fff1f2; color:#e11d48; }
.ac-card-info { flex:1; min-width:0; }
.ac-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0; line-height:1.3; }

/* Details */
.ac-card-details { display:flex; flex-wrap:wrap; gap:6px; }
.ac-detail { flex:1; min-width:70px; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:6px 10px; display:flex; flex-direction:column; gap:2px; }
.ac-detail-label { font-size:10px; color:$gray400; font-weight:500; text-transform:uppercase; letter-spacing:0.3px; }
.ac-detail-val { font-size:13px; color:$gray700; font-weight:700; }

/* Actions */
.ac-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.ac-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; i { font-size:10px; } }
.ac-btn-edit { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.ac-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }

/* Responsive */
@media(max-width:992px) { .ac-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .ac-grid { grid-template-columns:1fr; } }
</style>
